import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';

const TODAY_DATE = new Date().toISOString().split('T')[0];

function BankStatementPage() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ inflow: 0, outflow: 0, bank_charges: 0, net: 0, count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ start_date: '', end_date: '', party: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getBankbook(filters);
      setEntries(r.data?.entries || []);
      setTotals(r.data?.totals || { inflow: 0, outflow: 0, bank_charges: 0, net: 0, count: 0 });
    } catch (e) {
      setError('Failed to load bank statement: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';

  return (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          <div className="page-header">
            <h1 className="page-title"><Landmark size={24} style={{ marginRight: '0.5rem' }} /> Bank Statement</h1>
          </div>

          {error && <div className="error-container"><p>{error}</p></div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="card" style={{ padding: '1.2rem', textAlign: 'center', borderLeft: '4px solid var(--success-color)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><TrendingUp size={14} /> Bank In</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--success-color)' }}>{fmt(totals.inflow)}</div>
            </div>
            <div className="card" style={{ padding: '1.2rem', textAlign: 'center', borderLeft: '4px solid var(--error-color)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><TrendingDown size={14} /> Bank Out</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--error-color)' }}>{fmt(totals.outflow)}</div>
            </div>
            <div className="card" style={{ padding: '1.2rem', textAlign: 'center', borderLeft: '4px solid var(--warning-color)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><AlertCircle size={14} /> Bank Charges</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--warning-color)' }}>{fmt(totals.bank_charges)}</div>
            </div>
            <div className="card" style={{ padding: '1.2rem', textAlign: 'center', borderLeft: '4px solid var(--primary-color)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Net Bank Position</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: totals.net >= 0 ? 'var(--success-color)' : 'var(--error-color)' }}>{fmt(totals.net)}</div>
            </div>
            <div className="card" style={{ padding: '1.2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Entries</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{totals.count}</div>
            </div>
          </div>

          <div className="filter-bar">
            <div className="filter-group">
              <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>From</label>
              <input type="date" max={TODAY_DATE} className="form-input" value={filters.start_date} onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div className="filter-group">
              <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>To</label>
              <input type="date" max={TODAY_DATE} className="form-input" value={filters.end_date} onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))} />
            </div>
            <div className="filter-group">
              <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Party</label>
              <input type="text" className="form-input" value={filters.party} onChange={e => setFilters(p => ({ ...p, party: e.target.value }))} placeholder="Customer or vendor name" />
            </div>
            <button className="btn btn-secondary" onClick={() => setFilters({ start_date: '', end_date: '', party: '' })} style={{ height: '42px', alignSelf: 'flex-end' }}>Clear</button>
          </div>

          <div className="table-container">
            <table className="data-table" style={{ fontSize: '0.92rem' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Particulars</th>
                  <th>Ref</th>
                  <th>Party</th>
                  <th>Mode</th>
                  <th>Txn ID</th>
                  <th style={{ textAlign: 'right' }}>Credit (In)</th>
                  <th style={{ textAlign: 'right' }}>Debit (Out)</th>
                  <th style={{ textAlign: 'right' }}>Charges</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i}>
                    <td>{fmtDate(e.date)}</td>
                    <td>{e.particulars}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{e.ref}</td>
                    <td>{e.party}</td>
                    <td style={{ textTransform: 'capitalize' }}>{e.mode}</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{e.txn_id || '-'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--success-color)', fontWeight: e.inflow ? 600 : 400 }}>{e.inflow ? fmt(e.inflow) : '-'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--error-color)', fontWeight: e.outflow ? 600 : 400 }}>{e.outflow ? fmt(e.outflow) : '-'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--warning-color)' }}>{e.bank_charges ? fmt(e.bank_charges) : '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(e.balance)}</td>
                  </tr>
                ))}
                {entries.length === 0 && !loading && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>No bank entries in this period.</td></tr>
                )}
              </tbody>
              {entries.length > 0 && (
                <tfoot>
                  <tr style={{ background: 'var(--bg-secondary)', fontWeight: 700 }}>
                    <td colSpan={6} style={{ textAlign: 'right' }}>Totals</td>
                    <td style={{ textAlign: 'right', color: 'var(--success-color)' }}>{fmt(totals.inflow)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--error-color)' }}>{fmt(totals.outflow)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--warning-color)' }}>{fmt(totals.bank_charges)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(totals.net)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BankStatementPage;
